import { addMinutes } from 'date-fns';

/**
 * SHER-834 — how long a prescription lasts, and the single place that turns that into
 * `treatment-finish`.
 *
 * Two paths write a prescription row: SingleTreatmentSelector's manual sub-form and the
 * pharmacy picker. They used to answer "when does this end?" independently — the manual form
 * computed the date from the entered duration, the picker wrote null unconditionally — and that
 * divergence IS the bug: a null end date means "chronique / à long terme" on read
 * (computePrescriptionDuration), so the picker asserted a long-term treatment nobody had
 * declared. One function now owns the answer, so the two paths cannot disagree again.
 */

export const DAYS = 'days';
export const WEEKS = 'weeks';
export const MONTHS = 'months';

/** Minutes per unit, matching what the manual sub-form has always used. */
const UNIT_MINUTES: Record<string, number> = {
    [DAYS]: 1440,
    [WEEKS]: 10080,
    [MONTHS]: 43200,
};

/**
 * Either an explicit chronic treatment, or an amount + unit. There is deliberately no third
 * state that means "the clinician never said": a caller that has not asked cannot produce this
 * value, which is what keeps the unasked case out of the persisted row.
 */
export interface PrescriptionDuration {
    chronic: boolean;
    amount?: number | null;
    unit?: string | null;
}

/**
 * Whether a duration has actually been chosen. Used to gate a confirm, exactly as the manual
 * sub-form refuses to save while neither the duration boxes nor the "chronique" checkbox are
 * filled in.
 */
export function isDurationChosen(duration?: PrescriptionDuration | null): boolean {
    if (!duration) {
        return false;
    }
    return duration.chronic || (duration.amount != null && !!duration.unit);
}

/**
 * SHER-1029 — a length in days said the way a clinician writes it: the largest unit that divides
 * exactly, and days when neither does.
 *
 * **This is the one ladder.** `differenceDatesToString` (utils/index.jsx) delegates here, so the
 * picker, the prescriptions table and the printed ordonnance cannot disagree — they used to be
 * two copies, and they disagreed at seven days: the picker said "1 wk" for a row the ordonnance
 * printed as "7 days", on the fifth commonest length in the CHOM data (58 of 890). Aligning the
 * copies only bought a test that watched them; this removes the second copy instead.
 *
 * `weeks > 1` is why seven days stays in days. It reads as an oddity in isolation and is kept
 * deliberately: it is what the table and the ordonnance have always printed.
 *
 * The day count is **not** rounded on the way in. Rows whose start and finish differ by a
 * fractional number of days render as "29.958 days" today, and rounding them would silently
 * promote one to "1 month" — the `%` guards handle the fraction correctly by falling through.
 */
export function daysToAmountAndUnit(days: number): { amount: number; unit: string } {
    if (days > 0 && days % 30 === 0) {
        return { amount: days / 30, unit: MONTHS };
    }
    if (days > 0 && days % 7 === 0 && days / 7 > 1) {
        return { amount: days / 7, unit: WEEKS };
    }
    return { amount: days, unit: DAYS };
}

/**
 * The picker's adapter over that ladder: a server-derived duration, restated in the units the
 * clinician would have typed. Anything already answered — chronic, empty, or a non-day unit —
 * passes through untouched.
 */
export function normaliseDuration(duration?: PrescriptionDuration | null): PrescriptionDuration {
    if (!duration || duration.chronic || duration.amount == null || duration.unit !== DAYS) {
        return { ...(duration ?? { chronic: false, amount: null, unit: null }) };
    }
    return { chronic: false, ...daysToAmountAndUnit(duration.amount) };
}

/**
 * The row's `treatment-finish`.
 *
 * Null for a chronic treatment — that is what a missing end date has always meant on read, and
 * 71% of CHOM's own prescription rows (2.067 of 2.898, measured 2026-09-03) are stored that
 * way. Null is therefore a legitimate value; what is not legitimate is reaching it without the
 * clinician saying so, which `isDurationChosen` is there to prevent.
 */
export function prescriptionFinishDate(start: Date, duration?: PrescriptionDuration | null): Date | null {
    if (!duration || duration.chronic) {
        return null;
    }
    const unitMinutes = duration.unit ? UNIT_MINUTES[duration.unit] : undefined;
    if (duration.amount == null || !unitMinutes) {
        return null;
    }
    return addMinutes(start, duration.amount * unitMinutes);
}
